import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { useQuery, useMutation, useQueryClient } from '@tanstack/vue-query'
import api from '@/services/axiosApi'
import { Partner, IntegrationVersion } from '@/types/partner'

const partnerLogoOverrides = {
  beehiiv: '/img/onboarding/beehiiv.png',
  bento: '/img/onboarding/bento.png',
  encharge: '/img/onboarding/encharge.png',
  userlist: '/img/onboarding/userlist.png',
  gohighlevel: '/img/onboarding/gohighlevel.png',
  klaviyo: '/img/onboarding/klaviyo.svg',
  mailerlite: '/img/onboarding/mailerlite.png',
  pardot: '/img/onboarding/pardot.png'
}

/**
 * Helper to determine if a partner should use V2 endpoints
 */
const shouldUseV2Endpoints = (version: IntegrationVersion | undefined): boolean => {
  return version === 'v2' || version === 'prefer_v2'
}

export const useIntegrationStore = defineStore('integration', () => {
  const partners = ref<Partner[]>([])
  // Timestamp set when a brand-new integration is connected (not an update).
  // Used by SegmentGroupSync to distinguish "async initial load" from "user just connected."
  const newlyConnectedAt = ref<number | null>(null)
  const configStore = useConfigStore()
  const { currentTeamPid, currentTeam } = storeToRefs(configStore)

  const queryClient = useQueryClient()

  /**
   * @deprecated Use partner.integrationVersion instead for per-provider routing
   */
  const hasNewIntegration = computed(() => {
    return currentTeam.value?.uses_v2_integrations
  })

  const transformPartners = (partners: Partner[]) => {
    return partners.map((partner: Partner) => ({
      ...partner,
      logo:
        partnerLogoOverrides[partner.name] ||
        `/img/onboarding/${partner.name}.svg`
    }))
  }

  // Use unified endpoint that returns all providers with integrationVersion
  const partnerEndpoint = computed(() => {
    return `/api/${currentTeamPid.value}/unified-partners`
  })

  /**
   * Fetches and sets the list of partners using Tanstack Query
   */
  const partnersQuery = useQuery({
    queryKey: computed(() => ['partners', currentTeamPid.value]),
    queryFn: () =>
      api
        .get(partnerEndpoint.value)
        .then((response) => transformPartners(response.data.partners)),
    enabled: computed(() => !!currentTeamPid.value)
  })

  watchEffect(() => {
    if (partnersQuery.data.value) {
      partners.value = klona(unref(partnersQuery.data))
    }
  })

  // Force refetch when team changes
  watch(
    [currentTeamPid],
    () => {
      if (currentTeamPid.value) {
        partnersQuery.refetch()
      }
    }
  )

  /**
   * Save integration - routes to V1 or V2 based on partner.integrationVersion
   */
  const saveIntegrationMutation = useMutation({
    mutationFn: (variables: { partner: Partner; data: object }) => {
      const useV2 = shouldUseV2Endpoints(variables.partner.integrationVersion)

      if (useV2) {
        return api.post(
          `/api/${currentTeamPid.value}/connections/${variables.partner.name}`,
          {
            ...variables.data
          }
        )
      }

      return api.post(
        `/api/${currentTeamPid.value}/integrations/${variables.partner.name}`,
        {
          ...variables.data
        }
      )
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['partners', currentTeamPid.value]
      })

      const isNew = !variables.partner.hasExistingIntegration
      recordAnalyticsEvent(
        isNew
          ? AnalyticsEventTypes.INTEGRATION_CONNECTED
          : AnalyticsEventTypes.INTEGRATION_UPDATED,
        {
          partnerName: variables.partner.name,
          partnerLabel: variables.partner.label
        }
      )

      if (isNew) {
        newlyConnectedAt.value = Date.now()
      }
    }
  })

  const save = () => {
    const { mutateAsync, error, isPending } = saveIntegrationMutation
    return { mutateAsync, error, isPending }
    // saveIntegrationMutation.mutate({ partner, data })
  }

  /**
   * Remove integration - routes to V1 or V2 based on partner.integrationVersion
   */
  const removeIntegrationMutation = useMutation({
    mutationFn: (partner: Partner) => {
      // For removal, only use V2 endpoint if the integration IS v2
      // (not prefer_v2, which means no existing integration)
      const useV2 = partner.integrationVersion === 'v2'

      if (useV2) {
        return api.delete(
          `/api/${currentTeamPid.value}/connections/${partner.name}`
        )
      }

      return api.delete(
        `/api/${currentTeamPid.value}/integrations/${partner.name}`
      )
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['partners', currentTeamPid.value]
      })
    }
  })

  const remove = (partner: Partner) => {
    removeIntegrationMutation.mutate(partner, {
      onSuccess: () => {
        recordAnalyticsEvent(AnalyticsEventTypes.INTEGRATION_DISCONNECTED, {
          partnerName: partner.name,
          partnerLabel: partner.label
        })
      }
    })
  }

  /**
   * Getter for the integrated partner
   */
  const integratedPartner = computed(() =>
    partners.value.find((partner) => partner.hasExistingIntegration === true)
  )

  watch(
    integratedPartner,
    (newVal) => {
      if (!newVal) return
      swapOutEspPlaceholder(newVal)
    },
    { immediate: true }
  )

  const integratedPartnerName = computed(() => integratedPartner.value?.name)

  return {
    partners,
    integratedPartner,
    integratedPartnerName,
    newlyConnectedAt,
    isLoading: partnersQuery.isLoading,
    isError: partnersQuery.isError,
    save,
    remove,
    hasNewIntegration
  }
})
